import { Check, ChevronDown, ChevronUp, Download, Lock } from 'react-feather';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PaymentMethodInput } from '__generated__/graphql';

import { Order } from '../../helpers';
import { encodeUrl } from '../../../../common/helpers';
import { useParticipant } from '../../ParticipantProvider';
import BankTransferInstructions from '../../../../common/BankTransfer/BankTransferInstructions';
import DelayedInvoiceInfo from '../../../../common/BankTransfer/DelayedInvoiceInfo';
import PaymentMethodSelection from '../../../../common/Payments/PaymentMethodSelection';
import PlatformFeeLine from '../../../../common/Purchases/PlatformFeeLine';
import PurchaseLine from '../../../../common/Purchases/PurchaseLine';
import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';
import usePaymentMethods, { PaymentMethod } from '../../../../common/usePaymentMethods';
import useUrlState from '../../../../common/useUrlState';

export interface OrderSummaryProps {
  order: Order;
}

const OrderSummary = ({ order }: OrderSummaryProps) => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate, parseDate } = useLocale();
  const { event } = order;
  const { participant } = useParticipant();

  const { allowedPaymentMethods } = usePaymentMethods({
    country: order.country,
    enabledPaymentMethods: event.payment_methods,
    bankTransferAllowed: !!order.company_name || !event.require_company_for_bank_transfer,
  });

  const [urlState] = useUrlState<{ orderId?: string; paymentStatus?: string; }>({
    parse: (params, { string }) => ({
      ...params,
      orderId: string(params.orderId),
      paymentStatus: string(params.paymentStatus),
    }),
  });

  const [paymentMethod, setPaymentMethod] = useState<Partial<PaymentMethodInput>>({
    payment_method: null,
    issuer: null,
  });

  const [showingPaymentMethodSelection, setShowingPaymentMethodSelection] = useState(
    !order.pending_bank_transfer_payment,
  );

  const [redirecting, setRedirecting] = useState<boolean>(false);

  const startPayment = () => {
    setRedirecting(true);
    const query = encodeUrl({ sourceUrl: window.location.href });
    window.location.href = `/p/pay/${order.id}/${order.token}/${paymentMethod.payment_method}${paymentMethod.issuer ? `/${paymentMethod.issuer}` : ''}?${query}`;
  };

  const selectedPaymentMethod = paymentMethod.payment_method;
  const bankTransferBlocked = selectedPaymentMethod === PaymentMethod.BankTransfer
    && event.require_company_for_bank_transfer
    && !order.company_name;
  const validPaymentMethod = selectedPaymentMethod && !bankTransferBlocked;

  const invoiceDate = parseDate(order.invoice_date, { timezone: 'UTC', format: 'internal_date' });
  const dueDate = parseDate(order.due_date, { timezone: 'UTC', format: 'internal_date' });

  const passedOnFee = order.paid_passed_on_fee + order.remaining_passed_on_fee;

  const hasInvoice = !order.draft && (order.invoice_url || order.fee_invoice_url);
  const hasMultipleInvoices = hasInvoice && !!order.invoice_url && !!order.fee_invoice_url;

  return (
    <>
      <UI.GridContainer>
        {urlState.orderId && (
          <UI.Success>
            <UI.Strong>
              {t('order_confirmed_message')}
            </UI.Strong>
          </UI.Success>
        )}

        {urlState.paymentStatus && (
          <UI.Div>
            {urlState.paymentStatus === 'pending' && (
              <UI.Warning>
                {t('common:payments.payment_pending')}
              </UI.Warning>
            )}
            {urlState.paymentStatus !== 'pending' && (
              <UI.Error>
                {t('common:payments.payment_failed')}
              </UI.Error>
            )}
          </UI.Div>
        )}

        {order.pending_bank_transfer_payment && (
          <BankTransferInstructions payment={order.pending_bank_transfer_payment} />
        )}

        {order.draft && order.remaining_amount > 0 && order.start_bank_transfer_payment && (
          <DelayedInvoiceInfo invoiceDate={order.invoice_date} email={participant.email} />
        )}

        <UI.GridContainer sc={{ gutter: 0.5 }}>
          <UI.GridContainer sc={{ columns: '2fr 3fr', alignVertical: 'center' }}>
            <UI.Div>
              <UI.Legend>{t('order_number')}</UI.Legend>
            </UI.Div>
            <UI.Div>
              {order.invoice_id}
            </UI.Div>
          </UI.GridContainer>
          <UI.GridContainer sc={{ columns: '2fr 3fr', alignVertical: 'center' }}>
            <UI.Div>
              <UI.Legend>{t('invoice_date')}</UI.Legend>
            </UI.Div>
            <UI.Div>
              {formatDate(invoiceDate, { format: 'display_compact_date' })}
            </UI.Div>
          </UI.GridContainer>
          {order.due_date !== order.invoice_date && (
            <UI.GridContainer sc={{ columns: '2fr 3fr', alignVertical: 'center' }}>
              <UI.Div>
                <UI.Legend>{t('due_date')}</UI.Legend>
              </UI.Div>
              <UI.Div>
                {formatDate(dueDate, { format: 'display_compact_date' })}
              </UI.Div>
            </UI.GridContainer>
          )}

          {order.credited_amount > 0 && (
            <UI.GridContainer sc={{ columns: '2fr 3fr', alignVertical: 'center' }}>
              <UI.Div>
                <UI.Legend>{t('credited')}</UI.Legend>
              </UI.Div>
              <UI.Div>
                {formatCurrency(-order.credited_amount)}
              </UI.Div>
            </UI.GridContainer>
          )}

          {!order.draft && (
            <UI.GridContainer sc={{ columns: '2fr 3fr', alignVertical: 'center' }}>
              <UI.Div>
                <UI.Legend>{t('payment_status')}</UI.Legend>
              </UI.Div>
              <UI.Div>
                {order.remaining_amount === 0 && (
                  <UI.Label
                    sc={{
                      background: 'primary.50', color: 'primary.900', small: true, fontWeight: 600, my: -0.5,
                    }}
                  >
                    <UI.Icon>
                      <Check />
                    </UI.Icon>
                    {' '}
                    {t('paid')}
                  </UI.Label>
                )}
                {order.remaining_amount > 0 && (
                  <UI.Label
                    sc={{
                      background: 'info.100', color: 'info.900', small: true, fontWeight: 600, my: -0.5,
                    }}
                  >
                    <UI.Icon>
                      <UI.Icons.Pending />
                    </UI.Icon>
                    {' '}
                    {t('not_yet_received')}
                  </UI.Label>
                )}
              </UI.Div>
            </UI.GridContainer>
          )}
        </UI.GridContainer>

        {order.remaining_amount > 0 && !order.draft && (
          <>
            <UI.Div>
              {order.pending_bank_transfer_payment && (
                <UI.Button
                  sc={{ size: 'sm', active: showingPaymentMethodSelection }}
                  onClick={() => setShowingPaymentMethodSelection((showing) => !showing)}
                >
                  {t('choose_another_payment_method')}
                  {' '}
                  <UI.Icon>
                    {showingPaymentMethodSelection ? <ChevronUp /> : <ChevronDown />}
                  </UI.Icon>
                </UI.Button>
              )}
              <UI.AnimateHeight isVisible={showingPaymentMethodSelection}>
                <UI.GridContainer sc={{ mt: order.pending_bank_transfer_payment ? [3, 4] : undefined }}>
                  <PaymentMethodSelection
                    value={paymentMethod}
                    onChange={(paymentMethod) => setPaymentMethod(paymentMethod)}
                    paymentMethods={allowedPaymentMethods}
                  />

                  <UI.Button
                    sc={{ brand: 'secondary' }}
                    disabled={!validPaymentMethod || redirecting}
                    onClick={startPayment}
                  >
                    <UI.Icon>
                      <Lock />
                    </UI.Icon>
                    {' '}
                    {paymentMethod.payment_method === PaymentMethod.BankTransfer ? t('confirm') : t('pay')}
                    {' ('}
                    {formatCurrency(order.remaining_amount)}
                    )
                  </UI.Button>
                </UI.GridContainer>
              </UI.AnimateHeight>
            </UI.Div>
          </>
        )}
      </UI.GridContainer>

      {(order.summary.tickets.length > 0 || order.summary.products.length > 0) && (
        <UI.GridContainer sc={{ gutter: 0.75 }}>
          <UI.Legend>
            {t('purchases')}
          </UI.Legend>
          <UI.GridContainer sc={{ gutter: 0.5 }}>
            {order.summary.tickets.map((ticket, index) => (
              <PurchaseLine ticket={ticket} key={index} />
            ))}
            {order.summary.products.map((product, index) => (
              <PurchaseLine product={product} key={index} />
            ))}
          </UI.GridContainer>
          {passedOnFee > 0 && (
            <>
              <UI.HR />
              <PlatformFeeLine fee={passedOnFee} />
            </>
          )}
          <UI.HR sc={{ borderWidth: 2 }} />
          <UI.GridContainer sc={{ columns: '2fr 1fr' }}>
            <UI.Div sc={{ fontSize: 3, fontWeight: 500 }}>
              {t('total')}
            </UI.Div>
            <UI.Div sc={{ fontSize: 3, strong: true, textAlign: 'right' }}>
              {formatCurrency(order.invoice_amount)}
            </UI.Div>
          </UI.GridContainer>
        </UI.GridContainer>
      )}

      {hasInvoice && (
        <UI.GridContainer sc={{ gutter: 0.75 }}>
          <UI.Div>
            <UI.Legend>
              {hasMultipleInvoices ? t('invoices') : t('invoice')}
            </UI.Legend>
            {hasMultipleInvoices && (
              <UI.Div sc={{ mt: 2 }}>
                {t('multiple_invoices_description', { event: order.event.title })}
              </UI.Div>
            )}
          </UI.Div>
          <UI.FlexContainer sc={{ gap: 0.25 }}>
            {order.invoice_url && (
              <UI.AButton
                href={order.invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                sc={{ outline: true }}
              >
                <UI.Icon>
                  <Download />
                </UI.Icon>
                {' '}
                {hasMultipleInvoices ? t('order') : t('download_invoice')}
              </UI.AButton>
            )}
            {order.fee_invoice_url && (
              <UI.AButton
                href={order.fee_invoice_url}
                target="_blank"
                rel="noopener noreferrer"
                sc={{ outline: true }}
              >
                <UI.Icon>
                  <Download />
                </UI.Icon>
                {' '}
                {t('platform_fee')}
              </UI.AButton>
            )}
          </UI.FlexContainer>
        </UI.GridContainer>
      )}
    </>
  );
};

export default OrderSummary;
